/**
 * Catching `beforeinstallprompt`, which fires whether or not anyone is
 * listening yet.
 *
 * Chromium dispatches it once, early, as soon as it decides the site is
 * installable — routinely before React has hydrated. A listener registered in
 * a component effect is therefore a coin flip: on a warm cache the event has
 * already come and gone and the page's install button is lost for the rest of
 * the visit. The browser's own mini-infobar is the one thing *not* lost by
 * missing the event — suppressing it is what `preventDefault()` does, so a
 * page that never hears the event leaves it free to appear. Losing our button
 * is reason enough: that is why this is a module with a `watchInstallability`
 * meant to be called *before* `hydrateRoot`, from the app's entry module, and
 * not a hook.
 *
 * WHY THIS DESIGN IS THE SHARED ONE. Two install implementations reached this
 * repo: apps/retells/src/install/ and a `usePWAInstall` hook ported into this
 * package from sara/app. The hook attached its listener in a mount effect —
 * the coin flip above — and kept the handle in a ref, so a warm load usually
 * reported no native prompt at all; it also ate the event unconditionally,
 * and snoozed for ten days under unprefixed keys. This capture, the
 * three-strike policy in ./install-state.ts and the hook in
 * ../hooks/use-install-offer.ts are retells' design, promoted here intact,
 * and `usePWAInstall` is gone. An app that ports sara/app's install card
 * builds it on `useInstallOffer`.
 *
 * The event is also single-use: `prompt()` may be called on it once, and the
 * handle is spent afterwards whatever the visitor chose. So this module owns
 * exactly one of them, hands out no references, and clears itself the moment
 * it is used — a second caller gets `"unavailable"` rather than a rejected
 * promise from a stale handle.
 *
 * None of this is typed by the DOM lib (`beforeinstallprompt` is not in any
 * standard), so the event shape is declared here.
 */
import { isOfferSilenced, type InstallStateStore } from "./install-state";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/** What `promptInstall` settled on. `unavailable` means it never ran. */
export type InstallOutcome = "accepted" | "dismissed" | "unavailable";

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
let watching = false;

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/**
 * Start listening. Idempotent, and safe to call off-browser.
 *
 * `store` is the app's one install record (see `createInstallStateStore`):
 * it is what the suppression decision below reads, and where an install is
 * written down. Only the first call's store is used; a document has one
 * install offer.
 *
 * Never unsubscribes: these two listeners live as long as the document, the
 * same as the service worker registration next to the call site.
 */
export function watchInstallability(store: InstallStateStore): void {
  if (watching) return;
  if (typeof window === "undefined") return;
  watching = true;

  window.addEventListener("beforeinstallprompt", (event: Event) => {
    // Two decisions here, and only one of them is conditional.
    //
    // The handle is kept every time. It is the only way to open the browser's
    // install dialog from a button of ours, it is dispatched once, and an
    // event that was not held on to cannot be got back.
    //
    // `preventDefault()` is a different matter, because it is what suppresses
    // Chromium's own mini-infobar. Calling it is only defensible while the
    // site still has an ask of its own to make: without it the browser shows
    // its infobar at a moment of its choosing and the site's offer becomes a
    // second, redundant ask. But once ./install-state.ts has gone quiet — three
    // dismissals spent, or an asking inside the last thirty days — a page that
    // goes on eating this event is taking away an affordance and putting
    // nothing in its place, leaving the visitor with *less* install UI than
    // one that had never listened at all. So the suppression lasts exactly as
    // long as the offer does.
    //
    // Reading storage from here is not a render read, and does not put
    // hydration at risk. This is meant to run before `hydrateRoot`, outside
    // React entirely, and it feeds nothing that the first render
    // consults: `useInstallOffer` reads `canPromptNatively()` through
    // useSyncExternalStore, whose server snapshot (false) is what hydration
    // renders with. The prerendered markup and the first client render stay
    // identical whatever localStorage happens to say.
    if (!isOfferSilenced(store.read())) event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    notify();
  });

  // The offer is retired here, where the event lands, rather than by
  // whoever is subscribed: a store write belongs to the event, not to a
  // component's subscription callback, and the install must be recorded
  // whether or not the panel happens to be mounted.
  window.addEventListener("appinstalled", () => {
    installed = true;
    deferred = null;
    store.retire();
    notify();
  });
}

/** Subscribe to changes in what the two predicates below would return. */
export function subscribeInstallability(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Is there a live handle to the browser's own install dialog right now? */
export function canPromptNatively(): boolean {
  return deferred !== null;
}

/**
 * Has an install completed in this tab since it opened?
 *
 * Distinct from `isRunningInstalled()` in ./install-platform.ts, which asks whether
 * *this document* is the installed app. This one is about the browser tab that
 * did the installing and is still sitting there in a normal window afterwards
 * — where the offer must come down even though the display mode never changed.
 */
export function wasInstalledThisSession(): boolean {
  return installed;
}

/**
 * Show the browser's install dialog and wait for the answer.
 *
 * The handle is cleared before awaiting rather than after: `prompt()` spends
 * it either way, and a double-click on the install button must not reach a
 * second `prompt()` call on a used event.
 */
export async function promptInstall(): Promise<InstallOutcome> {
  const event = deferred;
  if (event === null) return "unavailable";
  deferred = null;
  notify();

  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome;
  } catch {
    // Chromium rejects if the dialog cannot be shown — most often because
    // something else already showed one for this event, which is a live
    // possibility now that a silenced offer lets the browser's own prompt
    // through (see `watchInstallability` above). Either way no dialog of ours
    // ever opened, and `unavailable` is how the caller is told: see
    // `costOfOutcome` in ../hooks/use-install-offer.ts, where a dialog that never
    // appeared costs the visitor nothing.
    return "unavailable";
  }
}
